import type * as BasketUpdated from '@ori-events/basket-updated';
import { Name as RequestShoppingContextEvent } from '@ori-events/request-shopping-context';
import { Name as ShoppingContextLoadedEvent } from '@ori-events/shopping-context-loaded';
import { Name as UserLogoutConfirmationEvent } from '@ori-events/user-logout-confirmation';
import { Name as UserLogoutConfirmedEvent } from '@ori-events/user-logout-confirmed';
import { Name as UserLogoutExecuteEvent } from '@ori-events/user-logout-execute';
import { Name as UserLogoutRequestEvent } from '@ori-events/user-logout-request';

import type { SomeLogger } from '../types/global';

type Context = Element | Window | typeof globalThis;

export default class LogoutProvider {
  readonly MAX_SHOPPING_CONTEXT_AWAIT_TIME_SECONDS = 10;

  /**
   * Element on which we listen for our events and also dispatch others' events.
   */
  readonly context: Context;

  /**
   * Logger (defaults to console) implementing basic console logging methods.
   */
  logger: SomeLogger;

  /**
   * Function called after all conditions are satisfied that does the logout (on BE and in sessionStorage).
   */
  private readonly logoutCallback: () => void;

  /**
   * Marks the phase between LogoutRequest and ShoppingBag's response to our ShoppingContext status request.
   * If the ShoppingBag fail to respond in MAX_SHOPPING_CONTEXT_AWAIT_TIME_SECONDS
   * we will no longer wait and logout user right away.
   */
  private isLogoutRequestSequenceInProgress = false;

  /**
   * Set to true on logout request event and is reset back to false
   * after short time defined by local constant MAX_SHOPPING_CONTEXT_AWAIT_TIME_SECONDS.
   */
  private isAwaitingShoppingContextInProgress = false;

  private wasLogoutShoppingContextLoadedInTime: boolean | undefined;

  constructor(logoutCallback: () => void, context: Context, logger: SomeLogger = console) {
    this.context = context;
    this.logoutCallback = logoutCallback;
    this.logger = logger;

    this.bindEvents();

    this.logger.info(`[LogoutProvider] Set up on context: `, context);
  }

  private bindEvents() {
    this.context.addEventListener(UserLogoutRequestEvent, (e) => {
      this.onLogoutRequest(e);
    });

    this.context.addEventListener(ShoppingContextLoadedEvent, (e) => {
      this.onShoppingContextLoaded(e);
    });

    this.context.addEventListener(UserLogoutConfirmedEvent, (e) => {
      this.onLogoutConfirmed(e);
    });

    this.context.addEventListener(UserLogoutExecuteEvent, (e) => {
      this.onLogoutExecute(e);
    });

    this.logger.debug(`[LogoutProvider.bindEvents] Events bound.`, {
      context: this.context,
      events: [
        UserLogoutRequestEvent,
        ShoppingContextLoadedEvent,
        UserLogoutConfirmedEvent,
        UserLogoutExecuteEvent,
      ],
    });
  }

  private startLogoutRequestSequence() {
    if (this.isLogoutRequestSequenceInProgress) {
      return;
    }

    this.isLogoutRequestSequenceInProgress = true;
    this.wasLogoutShoppingContextLoadedInTime = undefined;

    // Limit validity of logout request to short period of time.
    this.isAwaitingShoppingContextInProgress = true;
    setTimeout(() => {
      if (typeof this.wasLogoutShoppingContextLoadedInTime === 'undefined') {
        this.wasLogoutShoppingContextLoadedInTime = false;
        this.endLogoutRequestSequence();

        // Skip to UserLogoutExecuteEvent directly and not wait for shopping bag status.
        this.context.dispatchEvent(new CustomEvent(UserLogoutExecuteEvent));
      }
    }, this.MAX_SHOPPING_CONTEXT_AWAIT_TIME_SECONDS * 1000);

    this.logger.info(
      `[LogoutProvider.startLogoutRequestSequence] Logout sequence started, listening for ${ShoppingContextLoadedEvent} event in next ${this.MAX_SHOPPING_CONTEXT_AWAIT_TIME_SECONDS} seconds.`,
    );
  }

  private endLogoutRequestSequence() {
    if (!this.isLogoutRequestSequenceInProgress) {
      return;
    }

    this.isLogoutRequestSequenceInProgress = false;
    if (this.isAwaitingShoppingContextInProgress) {
      this.isAwaitingShoppingContextInProgress = false;
    }

    if (this.wasLogoutShoppingContextLoadedInTime === false) {
      this.logger.error(
        `[LogoutProvider.endLogoutRequestSequence] Event ${ShoppingContextLoadedEvent} failed to be fired in time (${this.MAX_SHOPPING_CONTEXT_AWAIT_TIME_SECONDS} seconds).`,
      );
    }
  }

  private onLogoutRequest(event: Event) {
    this.logger.debug(`[LogoutProvider.onLogoutRequest] event:`, event);
    if (this.isLogoutRequestSequenceInProgress) {
      this.logger.info(
        `[LogoutProvider.onLogoutRequest] Ignoring another request - log out sequence has already been started.`,
      );

      return;
    }

    this.startLogoutRequestSequence();
    this.context.dispatchEvent(new CustomEvent(RequestShoppingContextEvent));
  }

  private onShoppingContextLoaded(event: Event) {
    if (!this.isLogoutRequestSequenceInProgress || !this.isAwaitingShoppingContextInProgress) {
      return;
    }

    this.logger.debug(`[LogoutProvider.onShoppingContextLoaded] event:`, event);

    // Ignore more ShoppingContextLoaded events.
    this.isAwaitingShoppingContextInProgress = false;
    this.wasLogoutShoppingContextLoadedInTime = true;
    this.endLogoutRequestSequence();

    const bagHasItems =
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- could be null
      (event as CustomEvent<BasketUpdated.Data>).detail?.shoppingBag?.shoppingBagQuantity > 0;

    if (bagHasItems) {
      this.context.dispatchEvent(new CustomEvent(UserLogoutConfirmationEvent));
    } else {
      this.context.dispatchEvent(new CustomEvent(UserLogoutExecuteEvent));
    }
  }

  private onLogoutConfirmed(event: Event) {
    this.logger.debug(`[LogoutProvider.onLogoutConfirmed] event:`, event);
    this.context.dispatchEvent(new CustomEvent(UserLogoutExecuteEvent));
  }

  private onLogoutExecute(event: Event) {
    this.logger.debug(`[LogoutProvider.onLogoutExecute] event:`, event);

    // Actually do logout = call logout callback.
    this.logoutCallback();
  }
}
